import React, { useState, useEffect } from 'react';
import { supabase_get_templates, supabase_upsert_template, supabase_delete_template, extractErrorMessage } from '../services/supabaseService';
import { AutomationTemplate } from '../types';
import { RichTextEditor } from './RichTextEditor';
import { Plus, Loader2, LayoutTemplate, AlertCircle, CheckCircle2 } from 'lucide-react';

interface StandardSectionsSettingsProps {
    clientId: string;
}

export const StandardSectionsSettings: React.FC<StandardSectionsSettingsProps> = ({ clientId }) => {
    const [templates, setTemplates] = useState<AutomationTemplate[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [editingTemplate, setEditingTemplate] = useState<Partial<AutomationTemplate> | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    useEffect(() => {
        if (clientId) fetchTemplates();
    }, [clientId]);

    const fetchTemplates = async () => {
        setIsLoading(true);
        try {
            const dbTemplates = await supabase_get_templates();
            setTemplates(dbTemplates || []);
        } catch (e: any) {
            console.error("Error fetching templates:", e);
            setError(extractErrorMessage(e));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveTemplate = async () => {
        if (!editingTemplate || !editingTemplate.name) return;
        setIsSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const templateData = {
                id: editingTemplate.id,
                client_id: clientId,
                name: editingTemplate.name,
                type: 'Section', // explicitly typed for standard sections
                subject: editingTemplate.subject || '',
                content: editingTemplate.content || ''
            };

            const saved = await supabase_upsert_template(templateData);
            setEditingTemplate(saved);
            setSuccess("Sectie succcesvol opgeslagen!");
            setTimeout(() => setSuccess(null), 3000);
            await fetchTemplates();
        } catch (e: any) {
            setError(extractErrorMessage(e));
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center p-10">
                <Loader2 className="animate-spin text-slate-400" size={32} />
            </div>
        );
    }

    return (
        <div className="max-w-6xl space-y-8 animate-fadeIn h-[calc(100vh-140px)] flex flex-col">
            <div>
                <h2 className="text-xl font-black text-slate-900 tracking-tight">Standaard Secties</h2>
                <p className="text-sm text-slate-500 font-medium mt-1">
                    Maak herbruikbare HTML contentblokken. Ideaal voor veelgebruikte vacatureteksten of email disclaimers.
                </p>
                {error && <div className="bg-red-50 border border-red-100 text-red-600 p-4 rounded-xl flex items-center gap-3 mt-4"><AlertCircle size={20} /><span className="text-xs font-bold">{error}</span></div>}
                {success && <div className="bg-emerald-50 border border-emerald-100 text-emerald-600 p-4 rounded-xl flex items-center gap-3 mt-4"><CheckCircle2 size={20} /><span className="text-xs font-bold">{success}</span></div>}
            </div>

            <div className="grid grid-cols-12 gap-6 flex-1 min-h-[500px]">
                {/* Left Sidebar: List */}
                <div className="col-span-12 md:col-span-4 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-[70vh]">
                    <div className="p-6 border-b border-slate-100 flex justify-between items-center shrink-0">
                        <h3 className="text-xs font-black uppercase tracking-widest text-slate-900">Secties</h3>
                        <button onClick={() => setEditingTemplate({ name: 'Nieuwe Sectie', type: 'Section', content: '' })} className="w-8 h-8 flex items-center justify-center bg-slate-900 text-white rounded-lg hover:bg-black"><Plus size={16} /></button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
                        {templates.filter(t => t.type === 'Section').map(tmpl => (
                            <div key={tmpl.id} onClick={() => setEditingTemplate(tmpl)} className={`p-4 rounded-xl cursor-pointer border transition-all ${editingTemplate?.id === tmpl.id ? 'bg-red- border-red- shadow-sm' : 'bg-white border-slate-100 hover:border-slate-300'}`}>
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                                        <LayoutTemplate size={14} />
                                    </div>
                                    <div className="min-w-0">
                                        <h4 className="text-xs font-bold text-slate-900 truncate">{tmpl.name}</h4>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {templates.filter(t => t.type === 'Section').length === 0 && (
                            <div className="text-center p-6 text-slate-400">
                                <LayoutTemplate className="mx-auto mb-2 opacity-50" size={24} />
                                <p className="text-xs">Geen secties gevonden</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Area: Editor */}
                <div className="col-span-12 md:col-span-8 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-[70vh]">
                    {editingTemplate ? (
                        <div className="flex flex-col h-full">
                            <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex flex-col gap-4 sm:flex-row justify-between items-start sm:items-center shrink-0">
                                <input type="text" value={editingTemplate.name} onChange={e => setEditingTemplate({ ...editingTemplate, name: e.target.value })} className="bg-transparent text-lg font-black text-slate-900 outline-none w-full" placeholder="Sectie Naam" />
                                <div className="flex gap-2">
                                    <button onClick={() => setEditingTemplate(null)} className="px-4 py-2 text-slate-500 text-xs font-bold hover:text-slate-900">Annuleren</button>
                                    <button onClick={handleSaveTemplate} className="px-6 py-2 bg-slate-900 text-white rounded-lg text-xs font-black uppercase tracking-widest hover:bg-black flex items-center gap-2">
                                        {isSaving ? <Loader2 className="animate-spin" size={14} /> : 'Opslaan'}
                                    </button>
                                </div>
                            </div>
                            <div className="p-8 flex-1 overflow-y-auto space-y-6">
                                <div className="h-full flex flex-col pb-4">
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block pl-1">Inhoud (HTML Toegestaan)</label>
                                    </div>
                                    <RichTextEditor value={editingTemplate.content || ''} onChange={val => setEditingTemplate({ ...editingTemplate, content: val })} placeholder="Bouw hier je standaard sectie (tekst, afbeeldingen, lijsten)..." />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-full text-slate-300 flex-col gap-4">
                            <LayoutTemplate size={48} />
                            <p className="text-xs font-bold uppercase tracking-widest">Selecteer of maak een sectie aan linksonder</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
